/**
 * Peso y estatura en unidades imperiales.
 *
 * Espejo de `core/utils/body_measures.py` en el backend: el peso se guarda en
 * **libras** y la estatura en **pulgadas totales**, siempre como enteros.
 *
 * La estatura se acepta escrita como sea —5'10", 5 10, 5.10, 70— porque los
 * agentes la dictan en pies y pulgadas. Guardarla como número (y no como texto)
 * es lo que permite calcular IMC, filtrar y mandarla al carrier sin ambigüedad.
 */

export const MIN_HEIGHT_INCHES = 24
export const MAX_HEIGHT_INCHES = 96

// Rango plausible de peso en libras. Se valida por rango y no por cantidad de
// dígitos: un '0150' tipeado con cero adelante son 150 libras válidas.
export const MIN_WEIGHT_POUNDS = 1
export const MAX_WEIGHT_POUNDS = 1500

/**
 * Convierte una estatura escrita en cualquier formato a pulgadas totales.
 * Devuelve null si no se puede interpretar o queda fuera de rango humano.
 */
export const parseHeightToInches = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number') {
    const inches = Math.round(value)
    return inches >= MIN_HEIGHT_INCHES && inches <= MAX_HEIGHT_INCHES ? inches : null
  }

  const text = String(value).trim()
  if (!text) return null

  // 5'10"  ·  5' 10  ·  5-10  ·  5 10  ·  5.10  ·  5,10
  const feetInches = text.match(
    /^(\d+)\s*(?:'|ft|feet|pies|[-.,\s])\s*(\d+)\s*(?:"|''|in|inches|pulg)?$/i
  )
  if (feetInches) {
    const feet = Number(feetInches[1])
    const inches = Number(feetInches[2])
    if (inches > 11) return null
    const total = feet * 12 + inches
    return total >= MIN_HEIGHT_INCHES && total <= MAX_HEIGHT_INCHES ? total : null
  }

  // 5'  ·  5 ft  ·  5 pies
  const feetOnly = text.match(/^(\d+)\s*(?:'|ft|feet|pies)$/i)
  if (feetOnly) {
    const total = Number(feetOnly[1]) * 12
    return total >= MIN_HEIGHT_INCHES && total <= MAX_HEIGHT_INCHES ? total : null
  }

  // Solo dígitos: un número chico son pies; el resto, pulgadas.
  const digits = text.match(/^(\d+)\s*(?:"|''|in|inches|pulg)?$/i)
  if (digits) {
    const n = Number(digits[1])
    if (n <= 8) return n * 12
    if (n >= MIN_HEIGHT_INCHES && n <= MAX_HEIGHT_INCHES) return n
  }
  return null
}

/** 70 → `5' 10"`. Formato de presentación del valor guardado. */
export const formatInches = (totalInches: string | number | null | undefined): string => {
  const n = Number(totalInches)
  if (!n || Number.isNaN(n)) return ''
  const feet = Math.floor(n / 12)
  const inches = n % 12
  return `${feet}' ${inches}"`
}

/** Convierte un peso a libras enteras. Acepta "165", "165.4", "165 lb". */
export const parseWeightToPounds = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null

  const inRange = (n: number) =>
    n >= MIN_WEIGHT_POUNDS && n <= MAX_WEIGHT_POUNDS ? n : null

  if (typeof value === 'number') return inRange(Math.round(value))

  const match = String(value).trim().match(/^(\d+(?:[.,]\d+)?)\s*(?:lb|lbs|libras)?$/i)
  return match ? inRange(Math.round(Number(match[1].replace(',', '.')))) : null
}
